import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import contactBg from './../../images/contactForm.png';
import { FacebookIcon, InstagramIcon, WhatsappIcon } from 'icons';
import emailjs from 'emailjs-com';

export const Section5 = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    message: '',
  });

  const [loading, setLoading] = useState(false); // Loading state
  const [showSuccessModal, setShowSuccessModal] = useState(false); // Modal state

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const sendEmail = (e: any) => {
    e.preventDefault();
    setLoading(true); // Set loading to true when the form is submitted

    emailjs
      .sendForm(
        'service_6h824fa',
        'template_5a2tagp',
        e.target,
        'byDKIa2UX8pu-AM38',
      )
      .then(
        () => {
          setLoading(false); // Stop loading
          setShowSuccessModal(true); // Show the success modal
          setFormData({
            fullName: '',
            email: '',
            message: '',
          });
        },
        (error: any) => {
          console.error('Failed to send email.', error.text);
          setLoading(false); // Stop loading if the email fails
        },
      );
  };

  return (
    <>
      <Helmet>
        <title>Contact & Support — Sonnen Raffstoren</title>
        <meta
          name="description"
          content="Get in touch with Sonnen Raffstoren GmbH for professional consultations, service requests and project inquiries. Our contact section provides a direct channel to request quotations, technical documentation and on-site assessments. We prioritize quick responses and clear guidance to help you select the optimal shading system for your needs, covering design options, motorization, and integration with smart home systems. After installation, our support team delivers maintenance, scheduled inspections and warranty management to ensure long-term reliability and peak performance of your exterior blinds. Reach out to schedule a demonstration or to speak with a technical advisor about custom solutions and timelines."
        />
      </Helmet>
      <section className="w-full min-h-screen bg-gradient-to-b from-[#1E90AA] to-black flex justify-center items-center p-8">
        <div className="md:w-[50%] w-[100%] bg-white rounded-bl-[12px] rounded-tl-[12px] rounded-tr-[12px] shadow-lg flex flex-col lg:flex-row">
          {/* Contact Form */}
          <div className="w-full lg:w-[70%] p-10">
            <div className="mb-4">
              <h2 className="text-[24px] font-bold">
                {t('section5.contactForm.title')}
              </h2>
              <h3 className="text-[14px] font-normal">
                {t('section5.contactForm.subtitle')}
              </h3>
            </div>
            <form className="space-y-4" onSubmit={sendEmail}>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                placeholder={t('section5.contactForm.namePlaceholder')}
                className="w-full p-3 border-b border-gray-400 focus:outline-none focus:border-black"
                onChange={handleInputChange}
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                placeholder={t('section5.contactForm.emailPlaceholder')}
                className="w-full p-3 border-b border-gray-400 focus:outline-none focus:border-black"
                onChange={handleInputChange}
              />
              <textarea
                name="message"
                value={formData.message}
                placeholder={t('section5.contactForm.messagePlaceholder')}
                className="w-full p-3 border-b border-gray-400 focus:outline-none focus:border-black resize-none"
                aria-multiline
                onChange={handleInputChange}
              ></textarea>
              <button
                type="submit"
                className="w-full lg:w-auto bg-[#1E90AA] text-white py-3 px-8 hover:bg-[#176F8A] transition disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Sending...' : t('section5.contactForm.sendButton')}
              </button>
            </form>
          </div>

          {/* Details & Social Media */}
          <div
            className="w-full lg:w-[31%] p-6 flex flex-col lg:rounded-tr-[12px] rounded-bl-[12px] lg:rounded-bl-[0px] justify-between items-center bg-cover bg-center text-white"
            style={{
              backgroundImage: `url(${contactBg})`,
            }}
          >
            <p className="text-white text-center">
              {t('section5.contactDetails.message')}
            </p>
            <div className="flex space-x-4 mt-4">
              <a href="https://www.facebook.com/sonnenraffstoren">
                <FacebookIcon />
              </a>
              <a href="https://www.instagram.com/sonnenraffstoren">
                <InstagramIcon />
              </a>
              <a href="https://wa.me/+41799219200">
                <WhatsappIcon />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg text-center">
            <h3 className="text-lg font-bold mb-4">Success!</h3>
            <p>Your message has been sent successfully.</p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="mt-4 bg-[#1E90AA] text-white py-2 px-4 rounded hover:bg-[#176F8A]"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Section5;
